import _ from 'lodash';

// * Takes a hex value and changes it into an RGB value
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace('#', '')
  );
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getDate = (dateString) => {
  const arr = dateString.split('-');
  return `${months[arr[1] - 1]} ${arr[2]}, ${arr[0]}`;
};

export const getLocalizedLink = (link, locale) => {
  let internalLink;
  const lang = locale && locale.code ? locale.code : '';
  if (link && link.indexOf('http') === -1) {
    internalLink = link !== '/home' ? `${lang}/${link}` : `/${lang}`;
    if (internalLink.substr(0, 1) !== '/') internalLink = `/${internalLink}`;
  }

  return internalLink;
};

/**
 * links to PDFs/videos/audios are under mediaItem
 * @param {object} link - link object definition
 * @param {object} locale - locale information object
 */
export const getLinkUrl = (link, locale) => {
  const mediaLink = _.get(link, 'mediaAsset.file.url');

  // add a leading slash if it is needed
  let internalLink;

  const lang = locale && locale.code ? locale.code : '';
  if (
    link &&
    link.internalLink &&
    link.internalLink.slug &&
    link.internalLink.slug.indexOf('http') === -1
  ) {
    if (link.internalLink.parentPage && link.internalLink.parentPage[0].slug != `home`) {
      internalLink = getPath(link.internalLink.parentPage[0]);
    }

    internalLink =
      link.internalLink.slug.indexOf(lang) !== -1
        ? `/${link.internalLink.slug}` 
        : internalLink ? `/${lang}/${internalLink}/${link.internalLink.slug}` 
        : `/${lang}/${link.internalLink.slug}`;
    if (internalLink.substr(0, 1) !== '/') internalLink = `/${internalLink}`;
  } else if (typeof link === 'string') {
    internalLink = link.indexOf(lang) !== -1 ? `${link}` : `${lang}/${link}`;
    if (internalLink.substr(0, 1) !== '/') internalLink = `/${internalLink}`;
  }

  if (internalLink && link.queryString) {
    internalLink = `${link.internalLink.slug}${link.queryString}`;
  }

  const url =
    mediaLink || internalLink || (link ? link.externalLink : null) || link.linkURL || '/';
  return url;
};

export const getPressReleasesIds = (components) => {
  let pressReleasesIds = [];
  const newsBlock = components.find(
    (component) => component.contentType === 'News'
  );

  if (newsBlock && newsBlock.items) {
    const releasesBlock = newsBlock.items.find(
      (block) => block.componentStyle === 'article'
    );

    const pressReleases = _.get(releasesBlock, 'items[0].result');
    if (pressReleases) {
      pressReleasesIds = pressReleases.map((release) => release.id);
    }
  }
  return pressReleasesIds;
};

export const getIconLinks = (items) => {
  const iconLinks = items.map((item) => {
    return {
      linkURL: item.linkUrl,
      imageSrc: item.imageSrc.url,
      altText: item.text,
    };
  });
  return iconLinks;
};

export const getLocalizedStringValue = (values, lang) => {
  let val = values;
  if (values && typeof values === 'object') {
    const obj = values.find((o) => o.locale === lang);

    val = obj ? obj.value : values[0].value;
  }
  return val;
};

export const createSlug = (text) =>
  `${text.split('.').join('').split(' ').join('-').toLowerCase()}`;

/**
 * Sort an array by an order parameter
 *
 * @param {array} items
 */
export const sortByOrder = (items) => {
  if (items) {
    items.sort((a, b) => {
      let comparison = 1;
      if (a.order > b.order) {
        comparison = 1;
      } else if (a.order < b.order) {
        comparison = -1;
      }
      return comparison;
    });
  }
};
/**

 * Sort an array by an order parameter
 *
 * @param {array} items
 */
export const sortBy = (items, sortField) => {
  if (items) {
    items.sort((a, b) => {
      let comparison = 1;
      if (a[sortField] > b[sortField]) {
        comparison = 1;
      } else if (a[sortField] < b[sortField]) {
        comparison = -1;
      } else {
        // they are the same
        if (a.order > b.order) {
          comparison = 1;
        } else if (a.order < b.order) {
          comparison = -1;
        }
      }
      return comparison;
    });
  }
};

/**
 * Get a translation based on an ID
 *
 * @param {string} id - the id of the translation
 * @param {array} translations - list of translations
 * @param {object} locale - the current locale information
 * @param {string} defaultValue - what to return if there is nothing defined
 */
export const getTranslation = (id, translations, locale, defaultValue) => {
  try {
    const values = translations.filter((item) => item.contentfulid === id);
    return (
      (values.find((translation) => translation.node_locale === locale) || {}).copy || values.find((translation) => translation.node_locale === 'en-US').copy
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    // console.log('Translation not found', id);
    return {
      value: defaultValue,
    };
  }
};

/**
 * Get the components list from a localized component object
 * if the current locale isn't entered, default to enUS
 *
 * @param {object} component - localized component data
 * @param {object} locale - currentl locale object
 */
export const getLocalComponent = (component, locale) => {
  const localComponent = component.filter(item => item.node_locale === locale);
  return localComponent.length === 0 ? component.filter((item) => item.node_locale === 'en-US') : localComponent;
};

export const getPathFromId = (arr, id) => {
  let output = {};
  arr.forEach((o) => {
    if (o.id.indexOf(id) !== -1) {
      output.slug = o.slug;
      output.path = o.path.replace(/^\/|\/$/g, '');
    } else if (o.children && o.children.length && !output.slug) {
      output = getPathFromId(o.children, id);
    }
  });
  return output;
};

export const getPath = (page) => {
  let path = "";
  if (page.parentPage && page.parentPage[0].slug != 'home') {
    return path = `${getPath(page.parentPage[0])}/${page.slug}`;
  } else {
    path = `${page.slug}`;
  }
  return path;
}

export const getPathFromSlug = (arr, slug, locale) => {
  let output = {};
  // brands is all dynamic linking from salsify
  if (slug.indexOf('/brands/') !== -1) return { path: slug, slug };

  // remove leading slash
  let checkSlug = slug.replace(/^\/+/g, '');

  // remove the locale if it exists
  if (locale && slug.indexOf(locale.code) !== -1)
    checkSlug = checkSlug.split(`${locale.code}/`).join('');

  // if there is no arr, then exit
  if (!arr) return slug;

  arr.forEach((o) => {
    // if (o.slug.indexOf(checkSlug) !== -1) {
    if (o.slug.toLowerCase() === checkSlug.toLowerCase()) {
      output.slug = o.slug;
      output.path = o.path.replace(/^\/|\/$/g, '');
    } else if (o.children && o.children.length && !output.slug) {
      output = getPathFromSlug(o.children, slug, locale);
    }
  });
  return output;
};

/**
 * Format the date to be readable
 *
 * @param {string} rawDate UTC date
 */
export const formatDate = (rawDate) => {
  const d = new Date(rawDate);
  // get the full month
  const month = d.toLocaleString('default', { month: 'long' });

  let hr = d.getHours();
  let min = d.getMinutes();
  if (min < 10) {
    min = `0${min}`;
  }
  let ampm = 'AM';
  if (hr > 12) {
    hr -= 12;
    ampm = 'PM';
  }

  return `${month} ${d.getDate()}, ${d.getFullYear()} at ${hr}:${min} ${ampm} EST`;
};

// patternCard is used in Contact Us location section, Leadership page.
export const getPatternCardStyle = (index) => {
  let backgroundColor;
  let cardType;
  // colors every second card (adding 10 because division from 0 is not allowed)
  if ((index + 10) % 2 === 0) {
    backgroundColor = 'spinSecondaryFour';
    cardType = 'pattern-card';
  } else {
    backgroundColor = 'white';
    cardType = 'pattern-card white-card';
  }
  return { backgroundColor, cardType };
};

/**Get an object of the salsify product property names for different locales
 * @param {string} locale - current locale
 */
export const getLocaleBasedPropertyName = (locale) => {
  let propertyObj;
  switch (locale) {
    case 'fr-CA':
      propertyObj = {
        productName: 'frenchProductName',
        productDescription: 'frenchProductDescription',
        productContents: 'frenchContents',
        productFeatures: 'frenchFeatures',

      }
      break;
    default:
      propertyObj = {
        productName: 'productName',
        productDescription: 'productCopy',
        productContents: 'contents',
        productFeatures: 'features'
      }
  }
  return propertyObj;
}